import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  parcoursApi,
  classementsApi,
  financesApi,
} from "@/lib/api";
import {
  PageHeader,
  Card,
  Kpi,
  Btn,
  Badge,
  EmptyState,
} from "@/components/DashboardUI";
import {
  Users,
  Percent,
  Wallet,
  ClipboardList,
  Map,
  Zap,
  ArrowRight,
  Sparkles,
} from "lucide-react";

export default function Overview() {
  const navigate = useNavigate();
  const [parcoursList, setParcoursList] = useState([]);
  const [stats, setStats] = useState({
    nbGroupes: 0,
    nbTerminees: 0,
    scoreMoyen: 0,
    dureeMoyenneMin: 0,
    tauxCompletion: 0,
  });
  const [overview, setOverview] = useState({
    revenu: { total: 0 },
    reservations: { total: 0 },
  });

  useEffect(() => {
    (async () => {
      try {
        const [pl, st, ov] = await Promise.all([
          parcoursApi.list(),
          classementsApi.dashboard(),
          financesApi.overview(),
        ]);
        setParcoursList(pl);
        setStats(st);
        setOverview(ov);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const today = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="overview-page">
      <PageHeader
        chapter="Chapitre pro · Le journal"
        title="Vue d'ensemble"
        subtitle={`Lot & Célé · ${today}`}
        testId="overview-header"
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={<Users className="w-5 h-5" strokeWidth={1.5} />}
          value={stats.nbGroupes}
          label="Groupes totaux"
          accent="or"
          testId="kpi-groupes"
        />
        <Kpi
          icon={<Percent className="w-5 h-5" strokeWidth={1.5} />}
          value={`${stats.tauxCompletion}%`}
          label="Taux complétion"
          accent="vert"
          testId="kpi-completion"
        />
        <Kpi
          icon={<Wallet className="w-5 h-5" strokeWidth={1.5} />}
          value={`${(overview.revenu.total || 0).toFixed(2)} €`}
          label="Revenu total"
          accent="or"
          testId="kpi-revenu"
        />
        <Kpi
          icon={<ClipboardList className="w-5 h-5" strokeWidth={1.5} />}
          value={overview.reservations.total || 0}
          label="Réservations"
          accent="parchemin"
          testId="kpi-reservations"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Parcours */}
        <Card testId="parcours-list">
          <div className="flex items-center justify-between mb-5">
            <h2 className="flex items-center gap-2 font-display text-lg text-parchemin">
              <Map className="w-4 h-4 text-or" strokeWidth={1.5} />
              Parcours
            </h2>
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard/parcours")}
              data-testid="goto-parcours"
            >
              Voir tout
              <ArrowRight className="w-3 h-3" />
            </Btn>
          </div>
          {parcoursList.length === 0 ? (
            <EmptyState
              icon={<Sparkles className="w-6 h-6" />}
              title="Aucun parcours créé pour le moment."
              hint="Va dans Parcours ou Builder IA pour en créer un."
              testId="parcours-empty"
            />
          ) : (
            <ul className="space-y-2">
              {parcoursList.slice(0, 5).map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between p-3 rounded-sm bg-noir-100/70 border border-or/10 hover:border-or/30 transition"
                  data-testid={`parcours-item-${p.id}`}
                >
                  <div>
                    <div className="font-medium text-parchemin">{p.nom}</div>
                    <div className="text-xs text-parchemin/50 font-mono mt-0.5">
                      {p.ville} · {p._count?.etapes || 0} étapes · {p.nbCadenas} cadenas
                    </div>
                  </div>
                  <Badge tone={p.actif ? "success" : "neutral"}>
                    {p.actif ? "Actif" : "Brouillon"}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Activité live */}
        <Card testId="activity-panel">
          <div className="flex items-center gap-2 mb-5">
            <Zap className="w-4 h-4 text-vert-50" strokeWidth={1.5} />
            <h2 className="font-display text-lg text-parchemin">Journal live</h2>
            <span
              className="w-1.5 h-1.5 rounded-full bg-vert-50 animate-glow-pulse ml-auto"
              aria-hidden="true"
            />
          </div>
          {stats.nbGroupes === 0 ? (
            <EmptyState
              icon={<Zap className="w-6 h-6" />}
              title="En attente d'activité…"
              hint="Les sessions apparaîtront ici en temps réel."
              testId="activity-empty"
            />
          ) : (
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-or/10 rounded-sm bg-vert-900/30">
                  <div className="font-display text-2xl text-parchemin stat-graved">
                    {stats.nbGroupes}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-vert-50 mt-1">
                    Groupes
                  </div>
                </div>
                <div className="p-3 border border-or/10 rounded-sm bg-noir-100/50">
                  <div className="font-display text-2xl text-parchemin stat-graved">
                    {stats.nbTerminees}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-or mt-1">
                    Terminés
                  </div>
                </div>
              </div>
              <div className="mt-4 font-mono text-xs text-parchemin/50 flex items-center justify-between">
                <span>
                  Score moyen ·{" "}
                  <span className="text-or">{stats.scoreMoyen}</span>
                </span>
                <span>
                  Durée moy. ·{" "}
                  <span className="text-or">{stats.dureeMoyenneMin} min</span>
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
